// The web app's client for the core API, which seedbed-web forwards to
// seedbed-api (worker/forward.ts). Unlike `api()` — the admin surface, or the
// account worker's own /api/account routes — most of what this reads is
// public: the token rides along when there is a session and is simply absent
// when there is not.
//
// A 401 on a request that carried a token means the token went stale: refresh
// once and retry. If the refresh fails the session is over (it is cleared),
// and a public read is retried signed out rather than failed — the feed does
// not disappear because a month-old refresh token did.

import { accessToken, refreshSession } from "@web-shared/lib/account";
import { ApiError } from "@web-shared/lib/api";

export async function webApi<T>(path: string, init?: RequestInit): Promise<T> {
  const send = (token: string) =>
    fetch(`/api${path}`, {
      ...init,
      headers: {
        ...(init?.body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...init?.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  const token = accessToken();
  let res = await send(token);
  if (res.status === 401 && token) {
    const fresh = await refreshSession();
    res = await send(fresh?.accessToken ?? "");
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

/** JSON body helper: `webApi(path, json("POST", {...}))`. */
export function json(method: "POST" | "PATCH" | "DELETE" | "PUT", body?: unknown): RequestInit {
  return { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) };
}
